import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from './Layout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { mockDashboardStats, mockLeads, mockDeals, mockContacts } from '../data/mockData';
import { 
  BarChart3, 
  TrendingUp, 
  DollarSign,
  Users,
  UserCheck,
  Calendar,
  Download,
  Filter,
  Eye,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

const Reports = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) {
    return null;
  }

  const stats = mockDashboardStats;

  // Calculate performance metrics
  const thisMonthLeads = mockLeads.filter(lead => 
    new Date(lead.createdDate) >= new Date(new Date().setDate(1))
  ).length;

  const thisMonthDeals = mockDeals.length;
  const closedDeals = mockDeals.filter(deal => deal.stage === 'Closed Won').length;
  const winRate = thisMonthDeals > 0 ? ((closedDeals / thisMonthDeals) * 100).toFixed(1) : 0;

  const leadsBySource = mockLeads.reduce((acc, lead) => {
    acc[lead.source] = (acc[lead.source] || 0) + 1;
    return acc;
  }, {});

  const dealsByStage = mockDeals.reduce((acc, deal) => {
    acc[deal.stage] = (acc[deal.stage] || 0) + 1;
    return acc;
  }, {});

  const MetricCard = ({ title, value, subtitle, change, icon: Icon, color }) => (
    <Card className="hover:shadow-lg transition-shadow duration-200">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
            <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
            {change && (
              <div className="flex items-center mt-2">
                {change > 0 ? (
                  <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 text-red-500 mr-1" />
                )}
                <span className={`text-sm font-medium ${change > 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {change > 0 ? '+' : ''}{change}%
                </span>
                <span className="text-sm text-gray-500 ml-1">vs last period</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${color}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
            <p className="text-gray-600 mt-1">Analyze your sales performance and trends</p>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline">
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </Button>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Monthly Leads"
            value={thisMonthLeads}
            subtitle="New leads this month"
            change={12}
            icon={UserCheck}
            color="bg-blue-500"
          />
          <MetricCard
            title="Active Deals"
            value={thisMonthDeals}
            subtitle="Deals in pipeline"
            change={8}
            icon={TrendingUp}
            color="bg-green-500"
          />
          <MetricCard
            title="Win Rate"
            value={`${winRate}%`}
            subtitle="Closed won percentage"
            change={5}
            icon={BarChart3}
            color="bg-purple-500"
          />
          <MetricCard
            title="Total Contacts"
            value={mockContacts.length}
            subtitle="Active contacts"
            change={15}
            icon={Users}
            color="bg-orange-500"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Leads by Source */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Leads by Source
                <Button variant="ghost" size="sm">
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(leadsBySource).map(([source, count]) => (
                  <div key={source} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span className="font-medium text-gray-900">{source}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{count} leads</div>
                      <div className="text-sm text-gray-600">
                        {((count / mockLeads.length) * 100).toFixed(1)}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Deals by Stage */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Deals by Stage
                <Button variant="ghost" size="sm">
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(dealsByStage).map(([stage, count]) => (
                  <div key={stage} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${
                        stage === 'Qualification' ? 'bg-blue-500' :
                        stage === 'Proposal' ? 'bg-yellow-500' :
                        stage === 'Negotiation' ? 'bg-orange-500' :
                        'bg-green-500'
                      }`}></div>
                      <span className="font-medium text-gray-900">{stage}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{count} deals</div>
                      <div className="text-sm text-gray-600">
                        {((count / mockDeals.length) * 100).toFixed(1)}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Performance Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Performance Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <DollarSign className="h-12 w-12 mx-auto mb-4 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Revenue Pipeline</h3>
                <p className="text-2xl font-bold text-blue-600">{stats.totalRevenue}</p>
                <p className="text-sm text-gray-600 mt-1">Total potential revenue</p>
              </div>
              
              <div className="text-center p-6 bg-green-50 rounded-lg">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 text-green-600" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Conversion Rate</h3>
                <p className="text-2xl font-bold text-green-600">24.5%</p>
                <p className="text-sm text-gray-600 mt-1">Lead to customer conversion</p>
              </div>
              
              <div className="text-center p-6 bg-purple-50 rounded-lg">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Avg. Sales Cycle</h3>
                <p className="text-2xl font-bold text-purple-600">32 days</p>
                <p className="text-sm text-gray-600 mt-1">From lead to close</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Performers */}
        <Card>
          <CardHeader>
            <CardTitle>Top Performers This Month</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                { name: 'John Doe', deals: 8, revenue: '$450,000', performance: 'Excellent' },
                { name: 'Jane Smith', deals: 6, revenue: '$380,000', performance: 'Good' },
                { name: 'Mike Johnson', deals: 5, revenue: '$320,000', performance: 'Good' }
              ].map((performer, index) => (
                <div key={performer.name} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">{index + 1}</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{performer.name}</h4>
                      <p className="text-sm text-gray-600">{performer.deals} deals closed</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{performer.revenue}</p>
                    <Badge className={
                      performer.performance === 'Excellent' 
                        ? 'bg-green-100 text-green-800 border-green-200'
                        : 'bg-blue-100 text-blue-800 border-blue-200'
                    }>
                      {performer.performance}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Reports;